import Phaser from 'phaser'
import { submitScore, rankOf } from '@/services/ranking'
import { useGameStore } from '@/store/gameStore'
import { recordPlayLocal, claimHintFromServer } from '@/services/hintItems'
import { awardPoints, useShopItem } from '@/services/shop'
import { makeImageTextButton } from '@/ui/button'
import { AudioManager } from '@/services/AudioManager'

export class GameOver extends Phaser.Scene {
  constructor() { super('GameOver') }

  create(data: { score: number; stage: number }) {
    const { width, height } = this.scale

    this.add.image(width / 2, height / 2, 'bg_result').setDisplaySize(width, height)
    AudioManager.init(this)
    AudioManager.playBgm('bgm_result')

    // 신기록 판정(이전 최고점보다 높으면 축하 연출) — 챌린지+닉네임일 때만
    const { mode, nickname } = useGameStore.getState()
    // 챌린지 정상 종료 1회 = 플레이 1회. 1·3·5회째에 지급 시도 → 서버 IP 일일 한도(3개) 확인 후 지급.
    // 연습/중도 나가기는 GameOver에 오지 않으므로 자동으로 카운트에서 제외된다.
    if (mode === 'challenge' && recordPlayLocal()) {
      void claimHintFromServer().then(granted => {
        if (granted && this.scene.isActive()) {
          this.add.text(width / 2, height * 0.625, '🎁 힌트 아이템 +1', {
            fontFamily: 'Arial', fontSize: `${Math.round(width * 0.04)}px`, color: '#7ef0c2', fontStyle: 'bold',
            stroke: '#000000', strokeThickness: 3,
          }).setOrigin(0.5)
        }
      })
    }

    // 포인트 적립: 챌린지 점수의 10% (포인트 2배 장착 시 ×2)
    if (mode === 'challenge') {
      let mult = 1
      if (useGameStore.getState().point2xActive) {
        if (useShopItem('point2x')) mult = 2
        useGameStore.getState().set({ point2xActive: false })
      }
      const gained = awardPoints(data.score, mult)
      if (gained > 0) {
        this.add.text(width / 2, height * 0.585, `💰 포인트 +${gained.toLocaleString()}${mult === 2 ? ' (2배!)' : ''}`, {
          fontFamily: 'Arial', fontSize: `${Math.round(width * 0.045)}px`, color: '#ffe066', fontStyle: 'bold',
          stroke: '#000000', strokeThickness: 3,
        }).setOrigin(0.5)
      }
    }
    const prevBest = this.readBest()
    const prevRank = this.readRank() // 직전 게임의 등수(없으면 0)
    const isNewBest = mode === 'challenge' && !!nickname && nickname.trim().length > 0 &&
      data.score > prevBest && data.score > 0
    if (mode === 'challenge' && data.score > prevBest) this.writeBest(data.score)

    // 마스코트: 신기록이면 cheer, 아니면 sad
    const mascot = this.add.image(width / 2, height * 0.26, isNewBest ? 'mascot_cheer' : 'mascot_sad').setOrigin(0.5)
    const mW = Math.min(width * 0.46, mascot.width)
    mascot.setDisplaySize(mW, mW * (mascot.height / mascot.width))

    // 타이틀
    const title = this.add.text(width / 2, height * 0.45, isNewBest ? '신기록 달성! 🎉' : 'GAME OVER', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * (isNewBest ? 0.085 : 0.1))}px`,
      color: isNewBest ? '#ffd23f' : '#ffffff', fontStyle: 'bold',
      stroke: '#3a2a6a', strokeThickness: Math.round(width * 0.012),
    }).setOrigin(0.5)

    if (isNewBest) this.celebrate()

    // 점수 / 스테이지
    this.add.text(width / 2, height * 0.53, `점수 ${data.score}   ·   스테이지 ${data.stage}`, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.05)}px`, color: '#ffffff', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5)

    // 내 순위: 리본 배지 아이콘 + 노란 가운데 원 위에 순위 숫자
    const badgeW = width * 0.36
    const badgeCx = width / 2
    const badgeCy = height * 0.67
    const badge = this.add.image(badgeCx, badgeCy, 'rank_badge')
      .setDisplaySize(badgeW, badgeW).setVisible(false)
    // 노란 원 중심 위에 숫자(살짝 작게, 10시 방향으로 미세 조정)
    const numText = this.add.text(badgeCx + badgeW * 0.012, badgeCy - badgeW * 0.030, '', {
      fontFamily: 'Arial', fontSize: `${Math.round(badgeW * 0.26)}px`, color: '#1b3a6b', fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false)
    // 연습/계산중/실패 등 텍스트 상태(배지 대신)
    const statusText = this.add.text(width / 2, height * 0.64, '', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.05)}px`, color: '#ffe066',
      align: 'center', fontStyle: 'bold', stroke: '#000000', strokeThickness: 4,
    }).setOrigin(0.5)

    // 순위 숫자를 노란 원 안에 맞춰 표시(자릿수 많으면 축소)
    const showRank = (rank: number) => {
      statusText.setVisible(false)
      badge.setVisible(true)
      numText.setText(`${rank}`).setVisible(true)
      const maxW = badgeW * 0.5 // 노란 원 안쪽 폭
      numText.setScale(numText.width > maxW ? maxW / numText.width : 1)
    }

    // 버튼: 로비이동(홈) + 랭킹 (이미지 버튼, 동일 크기)
    makeImageTextButton(this, width / 2, height * 0.82, 'btn_home', '로비이동', () => this.scene.start('Lobby'),
      { width: width * 0.46, fontSize: width * 0.042 })
    makeImageTextButton(this, width / 2, height * 0.94, 'btn_rank', '랭킹', () => this.scene.start('Leaderboard'),
      { width: width * 0.46, fontSize: width * 0.042 })

    this.registry.set('lastResult', data)

    void (async () => {
      try {
        // 닉네임이 있는 챌린지만 기록/순위 계산(빈 닉네임은 DB 제약 위반 → 400)
        if (mode === 'challenge' && nickname && nickname.trim().length > 0) {
          statusText.setText('순위 계산 중...')
          await submitScore({
            nickname: nickname.trim(), score: data.score, stage: data.stage,
            token: useGameStore.getState().gameToken,
          })
          if (!this.scene.isActive()) return
          const myRank = await rankOf(data.score)
          if (!this.scene.isActive()) return
          showRank(myRank)
          // 순위 상승(직전 게임보다 등수가 좋아짐) → 신기록이 아니어도 축하(happy + 꽃가루)
          if (!isNewBest && prevRank > 0 && myRank < prevRank) {
            mascot.setTexture('mascot_happy')
            const mw = Math.min(width * 0.46, mascot.width)
            mascot.setDisplaySize(mw, mw * (mascot.height / mascot.width))
            title.setText('순위 상승! ⬆️').setColor('#ffd23f')
            this.celebrate()
          }
          this.writeRank(myRank) // 다음 게임 비교용으로 이번 등수 저장
          if (myRank === 1) AudioManager.play('high_score', 0.7)
        } else {
          statusText.setText('연습 모드 · 기록 미저장')
        }
      } catch {
        if (!this.scene.isActive()) return
        statusText.setText('순위를 불러오지 못했어요 😅')
      }
    })()
  }

  private readBest(): number {
    try { return parseInt(localStorage.getItem('10block_best') || '0', 10) || 0 } catch { return 0 }
  }
  private writeBest(score: number) {
    try { localStorage.setItem('10block_best', String(score)) } catch { /* noop */ }
  }
  private readRank(): number {
    try { return parseInt(localStorage.getItem('10block_last_rank') || '0', 10) || 0 } catch { return 0 }
  }
  private writeRank(rank: number) {
    try { localStorage.setItem('10block_last_rank', String(rank)) } catch { /* noop */ }
  }

  /** 신기록 축하: 꽃가루(위에서 떨어짐) + 폭죽(여러 번 터짐) */
  private celebrate() {
    const { width, height } = this.scale
    AudioManager.play('high_score', 0.6)
    const colors = [0xff5b6e, 0xffd23f, 0x4ade80, 0x60a5fa, 0xc084fc, 0xfb923c]
    let confettiSeed = 0

    // 꽃가루: 무작위 위치/크기/속도/회전으로 떨어짐
    const dropConfetti = () => {
      const count = 3 + Math.floor(Math.random() * 4)
      for (let i = 0; i < count; i++) {
        confettiSeed++
        const x = Math.random() * width
        const c = colors[Math.floor(Math.random() * colors.length)]
        const w = width * (0.012 + Math.random() * 0.012)
        const piece = this.add.rectangle(x, -20 - Math.random() * height * 0.1, w, w * 1.6, c).setDepth(60)
        piece.setAngle(Math.random() * 360)
        this.tweens.add({
          targets: piece,
          y: height + 30,
          x: x + (Math.random() - 0.5) * width * 0.25,
          angle: piece.angle + (Math.random() < 0.5 ? 1 : -1) * (180 + Math.random() * 540),
          duration: 2200 + Math.random() * 2000,
          ease: 'Sine.easeIn',
          onComplete: () => piece.destroy(),
        })
      }
    }
    const confettiTimer = this.time.addEvent({ delay: 120, repeat: 26, callback: dropConfetti })

    // 폭죽: 여러 지점에서 차례로 터짐
    const firework = (cx: number, cy: number) => {
      const color = colors[(confettiSeed + Math.round(cx)) % colors.length]
      const flash = this.add.circle(cx, cy, width * 0.03, 0xffffff, 0.9).setDepth(60)
      this.tweens.add({ targets: flash, scale: 2, alpha: 0, duration: 260, onComplete: () => flash.destroy() })
      for (let i = 0; i < 26; i++) {
        const ang = (Math.PI * 2 * i) / 26
        const spd = width * (0.16 + (i % 4) * 0.03)
        const p = this.add.circle(cx, cy, width * 0.012, color, 1).setDepth(60)
        this.tweens.add({
          targets: p,
          x: cx + Math.cos(ang) * spd,
          y: cy + Math.sin(ang) * spd + height * 0.04,
          alpha: 0,
          scale: 0.3,
          duration: 700 + (i % 5) * 120,
          ease: 'Cubic.easeOut',
          onComplete: () => p.destroy(),
        })
      }
    }
    const spots: [number, number][] = [
      [width * 0.3, height * 0.2], [width * 0.72, height * 0.16],
      [width * 0.5, height * 0.1], [width * 0.22, height * 0.3], [width * 0.8, height * 0.28],
    ]
    spots.forEach(([cx, cy], i) => this.time.delayedCall(i * 360 + 150, () => { if (this.scene.isActive()) firework(cx, cy) }))

    // 씬 종료 시 타이머 정리
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => confettiTimer.remove())
  }
}
